/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useLayoutEffect, useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { overrideForwardRef } from "../OverridableComponent";
import { useForkRef } from "../../../hooks";
import {
	computeRelativeOffset,
	getBoundingClientRect,
	getOwnerDocument,
	getScrollableParents,
	toPx
} from "../../../utils";

export interface VirtualElement {
	getBoundingClientRect: () => DOMRect;
	contextElement?: Element;
}

export type PopperPlacementType =
	| "auto-end"
	| "auto-start"
	| "auto"
	| "bottom-end"
	| "bottom-start"
	| "bottom"
	| "left-end"
	| "left-start"
	| "left"
	| "right-end"
	| "right-start"
	| "right"
	| "top-end"
	| "top-start"
	| "top";

export interface IPopperBaseProps {
	anchorEl?: null | VirtualElement | (() => VirtualElement);
	children?: React.ReactNode;
	open: boolean;
	placement?: PopperPlacementType;
	preventTransition?: boolean;
	className?: string;
	arrow?: boolean;
	safePadding?: number;
	autoWidth?: boolean;
}

const passive: AddEventListenerOptions & EventListenerOptions = { passive: true };

export const PopperBase = overrideForwardRef<IPopperBaseProps, "div">(
	(
		{
			Component,
			className,
			anchorEl,
			children,
			open,
			placement,
			preventTransition,
			safePadding,
			autoWidth,
			...restProps
		},
		ref
	) => {
		const baseClass = BEM("edt-popper-base");

		const popperRef = useRef<HTMLDivElement>();
		const forkRef = useForkRef(popperRef, ref);

		useLayoutEffect(() => {
			if (!popperRef?.current) {
				return;
			}

			const scrollableParents = anchorEl ? getScrollableParents(anchorEl) : [ document.body ];

			const updatePosition = () => {
				const style = popperRef.current.style;

				const anchorRect = getBoundingClientRect(
					anchorEl || getOwnerDocument(popperRef.current)?.body
				);

				if (!autoWidth && anchorEl) {
					style.minWidth = toPx(anchorRect.width);
				}

				const elementRect = getBoundingClientRect(popperRef.current);

				const relativeOffset = computeRelativeOffset({
					elementRect,
					anchorRect,
					placement
				});

				style.left = toPx(relativeOffset.x);
				style.top = toPx(relativeOffset.y);

				style.visibility = "visible";
			};

			const updateDebounced = () => {
				requestAnimationFrame(() => {
					updatePosition();
				});
			};

			updateDebounced();

			const unsubscribes = scrollableParents.map((el) => {
				el.addEventListener("scroll", updateDebounced, passive);
				return () => {
					el.removeEventListener("scroll", updateDebounced, passive);
				};
			});

			return () => {
				unsubscribes.forEach((unsubscribe) => unsubscribe());
			};
		}, [ open ]);

		if (className) {
			baseClass.add(className);
		}

		return (
			<Component {...restProps} className={baseClass.toClassName()} ref={forkRef}>
				{children}
			</Component>
		);
	}
);

PopperBase.defaultProps = {
	Component: "div",
	placement: "bottom",
	safePadding: 5
};
